import { z } from 'zod';

import { getBirthNumberErrorMessage } from '../../components/AppAdvancedSearch/helpers/getBirthNumberErrorMessage';
import { createPhoneSchema } from '../../const/zod/phoneSchema';
import {
	FIRST_NAME_OR_LAST_NAME_MAX_LENGTH,
	FIRST_NAME_OR_LAST_NAME_REGEX,
	IDENTITY_CARD_NUMBER_LENGTH,
	IDENTITY_CARD_NUMBER_REGEX,
	EMAIL_OR_EMPTY_SCHEMA,
} from '../../const/zod/zod';
import { defaultPagingSchema } from '../schemas/defaultPagingSchema';

import { searchClientSortSchema } from './searchClientSortSchema';

const DEFAULT_ERROR_MESSAGE = 'default error message';

export const advancedSearchResultFormSchema = defaultPagingSchema
	.extend({
		lastName: z
			.string()
			.max(FIRST_NAME_OR_LAST_NAME_MAX_LENGTH)
			.optional()
			.nullable()
			.refine((val) => !val || FIRST_NAME_OR_LAST_NAME_REGEX.test(val))
			.catch(''),
		firstName: z
			.string()
			.max(FIRST_NAME_OR_LAST_NAME_MAX_LENGTH)
			.optional()
			.nullable()
			.refine((val) => !val || FIRST_NAME_OR_LAST_NAME_REGEX.test(val))
			.catch(''),
		identityCardNumber: z
			.string()
			.max(IDENTITY_CARD_NUMBER_LENGTH)
			.optional()
			.nullable()
			.refine((val) => !val || IDENTITY_CARD_NUMBER_REGEX.test(val))
			.catch(''),
		birthNumber: z
			.string()
			.optional()
			.nullable()
			.refine((value) => {
				const errorMessage = getBirthNumberErrorMessage({
					value,
					numberErrorText: DEFAULT_ERROR_MESSAGE,
					noSlashText: DEFAULT_ERROR_MESSAGE,
					getBirthNumberRemainderText: () => DEFAULT_ERROR_MESSAGE,
					birthNumberInvalidText: DEFAULT_ERROR_MESSAGE,
					getBirthNumberLengthText: () => DEFAULT_ERROR_MESSAGE,
				});

				if (!errorMessage) return true;

				return false;
			})
			.catch(''),
		birthday: z.string().optional().nullable().or(z.literal('')).catch(''),
		phone: createPhoneSchema({
			getPhoneLengthError: () => DEFAULT_ERROR_MESSAGE,
		}).catch(undefined),
		email: EMAIL_OR_EMPTY_SCHEMA.optional().nullable().catch(''),
		pidUuid: z.coerce
			.string()
			.refine((value) => z.uuid().safeParse(value).success)
			.optional()
			.nullable()
			.or(z.literal(''))
			.catch(''),
		sort: searchClientSortSchema,
	})
	.refine((data) =>
		Object.values(data).some((value) => {
			if (value === null || value === undefined) return false;
			if (typeof value === 'string' && value.trim() === '') return false;
			return true;
		}),
	);

export type AdvancedSearchResultForm = z.infer<
	typeof advancedSearchResultFormSchema
>;
